import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, RotateCcw, HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface PlateSelectorProps {
  currentWeight: number | undefined;
  onChange: (newWeight: number | undefined) => void;
  disabled?: boolean;
  size?: 'sm' | 'xs';
}

export const PlateSelector: React.FC<PlateSelectorProps> = ({
  currentWeight,
  onChange,
  disabled = false,
  size = 'xs',
}) => {
  const plates = [45, 25, 10, 5, 2.5];

  const handleAddSingle = (plate: number) => {
    const current = currentWeight || 0;
    onChange(current + plate);
  };

  const handleAddPair = (plate: number) => {
    const current = currentWeight || 0;
    onChange(current + plate * 2);
  };

  const handleClear = () => {
    onChange(undefined);
  };

  const handleSetStandardBarbell = () => {
    onChange(45);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`${
            size === 'xs' ? 'h-7 w-7 px-0 text-[10px]' : 'h-8 w-8 px-0 text-xs'
          } font-mono border-primary/20 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/40 shrink-0`}
          title="Plate Stacker Quick-Add"
        >
          +P
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        align="center" 
        className="w-64 p-3 bg-card border border-border rounded-sm shadow-md font-mono space-y-3 z-50"
      >
        <div className="flex items-center justify-between border-b border-border/40 pb-1.5">
          <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Plate Stacker</span>
          <span className="text-[10px] text-muted-foreground font-bold">
            CURR: <span className="text-foreground">{currentWeight ?? 0} lbs</span>
          </span>
        </div>

        {/* Quick Presets */}
        <div className="grid grid-cols-2 gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleSetStandardBarbell}
            className="h-6 text-[9px] font-mono border-border hover:bg-secondary justify-center"
          >
            Barbell (45 lbs)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleClear}
            className="h-6 text-[9px] font-mono border-destructive/20 text-destructive hover:bg-destructive/5 justify-center flex items-center gap-1"
          >
            <RotateCcw className="h-2.5 w-2.5" /> Clear
          </Button>
        </div>

        {/* Add Pairs (Barbell Mode) */}
        <div className="space-y-1">
          <div className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">Add Pair (+2x)</div>
          <div className="grid grid-cols-5 gap-1">
            {plates.map((plate) => (
              <Button
                key={`pair-${plate}`}
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => handleAddPair(plate)}
                className="h-7 text-[9px] font-mono px-0 hover:bg-primary/10 hover:text-primary border border-transparent hover:border-primary/20"
                title={`Add two ${plate} lb plates (+${plate * 2} lbs)`}
              >
                +{plate * 2}
              </Button>
            ))}
          </div>
        </div>

        {/* Add Singles */}
        <div className="space-y-1">
          <div className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">Add Single (+1x)</div>
          <div className="grid grid-cols-5 gap-1">
            {plates.map((plate) => (
              <Button
                key={`single-${plate}`}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleAddSingle(plate)}
                className="h-7 text-[9px] font-mono px-0 border-border/60 hover:bg-secondary"
                title={`Add one ${plate} lb plate (+${plate} lbs)`}
              >
                +{plate}
              </Button>
            ))}
          </div>
        </div>

        <div className="text-[8px] text-muted-foreground leading-normal border-t border-border/40 pt-1.5 flex items-center gap-1">
          <HelpCircle className="h-2.5 w-2.5 shrink-0" />
          <span>Tap plates to stack. Barbell starts at 45 lbs.</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
